import { Badge, Box, HStack, Tooltip } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { ContentTranslationUiStatus } from '@duke-hq/shared-constants/contentTranslationStatus';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';
import {
  generationDetailsLabel,
  type TranslationGenerationDetails,
} from './TranslationGenerationDetails';
import { isArrived, useStatusTransition } from './useStatusTransition';
import { useTransitionCounter } from './useTransitionCounter';
import {
  breathDelayMs,
  BREATH_ANIMATION,
  BREATH_KEYFRAMES,
  translationsMotion,
} from './translationsMotion';

const { durations, easings } = translationsMotion;

export function isTranslatedStatus(status: ContentTranslationUiStatus): boolean {
  return status === 'available' || status === 'available-partial';
}

export function StatusLabel({ status }: { status: ContentTranslationUiStatus }) {
  switch (status) {
    case 'not-translated':
      return <Trans>Not translated</Trans>;
    // Queued and generating share a word; the breathing dot conveys the activity.
    case 'queued':
    case 'generating':
      return <Trans>Translating</Trans>;
    case 'available':
      return <Trans>Translated</Trans>;
    case 'available-partial':
      return <Trans>Partly translated</Trans>;
    case 'stale':
      return <Trans>Needs review</Trans>;
    case 'failed':
      return <Trans>Failed</Trans>;
    case 'cancelled':
      return <Trans>Cancelled</Trans>;
  }
}

function isArrival(
  previous: ContentTranslationUiStatus | null,
  next: ContentTranslationUiStatus,
): boolean {
  const wasInFlight = previous === 'queued' || previous === 'generating';
  return wasInFlight && isArrived(next);
}

export function StatusLedDot({
  status,
  size = pxToRem(8),
  breathIndex = 0,
}: {
  status: ContentTranslationUiStatus;
  size?: string;
  breathIndex?: number;
}) {
  const { dot, dotTransition, shouldBreathe, motionDisabled } = useStatusTransition(status);
  const arrivalKey = useTransitionCounter(status, isArrival, !motionDisabled);
  const justLanded = arrivalKey > 0;
  // Partly translated renders as a half-filled ring: in the done family, visibly
  // not all the way there. Fully translated stays a plain solid dot.
  const isHalfDot = status === 'available-partial';

  return (
    <Box
      position="relative"
      boxSize={size}
      flexShrink={0}
      role="presentation"
      aria-hidden
      // Clip the pop overshoot, else a sliver paints into the chip gap as a faint second dot.
      overflow="hidden"
      borderRadius="full"
      sx={
        shouldBreathe
          ? {
              animation: BREATH_ANIMATION,
              animationDelay: `${breathDelayMs(breathIndex)}ms`,
              ...BREATH_KEYFRAMES,
            }
          : undefined
      }
    >
      <Box
        key={arrivalKey}
        position="relative"
        boxSize="full"
        borderRadius="full"
        bg={isHalfDot ? 'transparent' : dot}
        boxShadow={isHalfDot ? `inset 0 0 0 1.5px ${dot}` : undefined}
        transition={dotTransition}
        transformOrigin="center"
        // The "click into place" overshoot, keyed by arrival count so it replays
        // on each landing, never on mount of an already-done row.
        sx={
          justLanded
            ? {
                animation: `translationDotPop ${durations.settle}ms ${easings.emphasised}`,
                '@keyframes translationDotPop': {
                  '0%': { transform: 'scale(1)' },
                  '45%': { transform: 'scale(1.14)' },
                  '100%': { transform: 'scale(1)' },
                },
              }
            : undefined
        }
      >
        {isHalfDot ? (
          // Left half filled; the parent's full border-radius clips it to a semicircle.
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            w="50%"
            bg={dot}
            transition={dotTransition}
          />
        ) : null}
      </Box>
    </Box>
  );
}

export function TranslationStatusChip({
  status,
  breathIndex = 0,
  generationDetails,
}: {
  status: ContentTranslationUiStatus;
  breathIndex?: number;
  generationDetails?: TranslationGenerationDetails;
}) {
  const { bg, color, borderColor, chipTransition } = useStatusTransition(status);

  const chip = (
    <Badge
      variant="pill"
      fontSize="xs"
      fontWeight="semibold"
      cursor="default"
      data-testid={testIds.statusChip}
      // Tint from resolved ramp tokens, not `colorScheme`: a class swap can't be
      // transitioned, so this lets the recolour bleed through teal.
      bg={bg}
      color={color}
      boxShadow={`inset 0 0 0px 1px ${borderColor}`}
      transition={chipTransition}
    >
      <HStack spacing="1.5" as="span">
        <StatusLedDot status={status} breathIndex={breathIndex} />
        <StatusLabel status={status} />
      </HStack>
    </Badge>
  );

  const label = generationDetailsLabel(generationDetails);
  if (label === null) {
    return chip;
  }
  return (
    <Tooltip label={label} openDelay={200}>
      {chip}
    </Tooltip>
  );
}

// Shown instead of the status chip when no course containing the resource offers
// the language: its generation status is irrelevant, so this reads "out of scope"
// with a neutral pill and a hollow dot. The tooltip carries the full reason,
// since a resource can sit in several courses.
export function NotOfferedChip() {
  return (
    <Tooltip label={t`No course using this resource offers this language.`} openDelay={200}>
      <Badge
        variant="pill"
        fontSize="xs"
        fontWeight="semibold"
        cursor="default"
        bg="gray.50"
        color="gray.500"
        boxShadow="inset 0 0 0px 1px var(--chakra-colors-gray-200)"
        data-testid={testIds.notOfferedChip}
      >
        <HStack spacing="1.5" as="span">
          <Box
            boxSize={pxToRem(8)}
            borderRadius="full"
            flexShrink={0}
            role="presentation"
            aria-hidden
            boxShadow="inset 0 0 0 1.5px var(--chakra-colors-gray-400)"
          />
          <Trans>Not offered</Trans>
        </HStack>
      </Badge>
    </Tooltip>
  );
}
